import React, { useEffect, useState } from 'react';
import { Mail, Shield, Share2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { tabService, type WorkspaceRole } from '@/services/tabs';
import { toast } from 'sonner';

interface CreateSharedSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROLES: { value: WorkspaceRole; label: string; description: string }[] = [
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to shared tabs.' },
  { value: 'editor', label: 'Editor', description: 'Can share and remove tabs.' },
  { value: 'admin', label: 'Admin', description: 'Can invite and manage members.' },
];

function parseEmails(value: string): string[] {
  return [...new Set(
    value
      .split(/[,;\n]+/)
      .map((part) => part.trim().toLowerCase())
      .filter((part) => part.length > 0)
  )];
}

export const CreateSharedSessionModal: React.FC<CreateSharedSessionModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('New Shared Session');
  const [emailsText, setEmailsText] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('viewer');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setName('New Shared Session');
      setEmailsText('');
      setRole('viewer');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim() || 'New Shared Session';
    const emails = parseEmails(emailsText);
    const invalid = emails.filter((email) => !EMAIL_REGEX.test(email));
    if (invalid.length > 0) {
      setError(`Invalid email address: ${invalid[0]}`);
      return;
    }

    setSubmitting(true);
    try {
      const workspace = await tabService.createWorkspace(trimmedName);
      const failures: string[] = [];
      for (const email of emails) {
        try {
          await tabService.inviteToWorkspace(workspace.id, email, role);
        } catch (err) {
          failures.push(`${email}: ${(err as Error).message ?? 'invite failed'}`);
        }
      }
      if (emails.length > 0) {
        toast.success(
          failures.length === 0
            ? `Session created and ${emails.length} invitation${emails.length === 1 ? '' : 's'} emailed`
            : `Session created; ${emails.length - failures.length} invite(s) sent`
        );
      } else {
        toast.success('Session created');
      }
      if (failures.length > 0) {
        toast.error(failures[0]);
      }
      onCreated?.();
      onClose();
    } catch (err) {
      const msg = (err as Error)?.message ?? 'Failed to create session';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share a new session" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-khiimori-text-muted">
          Create a shared tab session and optionally email collaborators an invite link right away.
        </p>

        <label className="block">
          <span className="text-sm font-medium text-khiimori-text-primary mb-1 block">Session name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Research sprint"
            autoFocus
            required
            className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-khiimori-text-primary flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4" /> Collaborator emails
            <span className="text-xs font-normal text-khiimori-text-muted">(optional)</span>
          </span>
          <textarea
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            placeholder="alice@company.com, bob@company.com"
            rows={3}
            className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          />
          <span className="text-xs text-khiimori-text-muted mt-1 block">
            Separate multiple emails with commas or new lines.
          </span>
        </label>

        {emailsText.trim() && (
          <div>
            <span className="text-sm font-medium text-khiimori-text-primary flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4" /> Default role for invitees
            </span>
            <div className="space-y-2">
              {ROLES.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                    role === r.value
                      ? 'border-primary-500/50 bg-primary-500/10'
                      : 'border-khiimori-border hover:bg-khiimori-bg-elevated/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="create-session-role"
                    value={r.value}
                    checked={role === r.value}
                    onChange={() => setRole(r.value)}
                    className="mt-1"
                  />
                  <span className="flex-1">
                    <span className="text-sm font-medium text-khiimori-text-primary">{r.label}</span>
                    <span className="block text-xs text-khiimori-text-muted">{r.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} className="gap-1.5">
            <Share2 className="w-4 h-4" />
            {submitting ? 'Creating…' : 'Create & invite'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSharedSessionModal;
